// TES-1312 — before the note is composed for Webdoc, every heading that still
// holds the untouched deterministic prefill (raw "label: value (source date)"
// lines, TES-1050) is summarised through the same per-heading AI call the
// "Sammanfatta" button makes. Emelie (TES-1240):
//   "Råa frågor och svar får aldrig klistras in i journalen."
// Text the doctor wrote, edited, cleared or already replaced with an AI draft
// is never sent and never replaced. When the AI cannot summarise a heading
// (switched off, fell back, failed), that heading is reported as still raw so
// the caller stops before composing instead of pasting raw answers.
//
// Pure on purpose (type imports and one pure sibling) so `node --test` runs it.
import type { JournalSectionAiResult } from "@care-by-ella/contracts";
import { isUntouchedPrefill } from "./journal-ai-gate";

export interface AutoSummaryBox {
  key: string;
  /** The box's text as it stands on screen. */
  current: string;
  /** The prefill the box was built from, as text, if any. */
  prefill: string | null;
}

export interface AutoSummaryOutcome {
  /** Headings whose prefill was replaced by the AI's summary, in order. */
  summarised: string[];
  /** Headings still holding raw prefill: AI off, fell back or failed. */
  raw: string[];
}

/** The headings, in order, whose box is still exactly its prefill. */
export function untouchedPrefillKeys(
  boxes: readonly AutoSummaryBox[],
): string[] {
  return boxes.filter(isUntouchedPrefill).map((b) => b.key);
}

export async function summariseUntouchedPrefill(input: {
  boxes: readonly AutoSummaryBox[];
  /** The admin's support view: never generates anything. */
  readOnly: boolean;
  /** The per-heading summary call; absent when the AI is not offered. */
  generate?: (key: string) => Promise<JournalSectionAiResult>;
  /**
   * Reads a box as it stands now: one the doctor started typing in while a
   * call was out is left as he wrote it.
   */
  currentOf: (key: string) => string;
  /** Writes the summary into the box (the same path as "Använd"). */
  apply: (key: string, text: string) => void;
  /** The heading now being summarised (1-based) and how many there are. */
  onProgress?: (current: number, total: number) => void;
  /** Errors that must stop the run (a lapsed session) rather than count as a failure. */
  isFatal?: (e: unknown) => boolean;
}): Promise<AutoSummaryOutcome> {
  if (input.readOnly) return { summarised: [], raw: [] };
  const prefillOf = new Map(input.boxes.map((b) => [b.key, b.prefill]));
  const stillUntouched = (key: string) =>
    isUntouchedPrefill({
      current: input.currentOf(key),
      prefill: prefillOf.get(key) ?? null,
    });
  const targets = untouchedPrefillKeys(input.boxes);
  if (!input.generate) return { summarised: [], raw: targets };

  const summarised: string[] = [];
  const failed: string[] = [];
  // One heading at a time: the API takes one section per call, and the
  // provider is not asked for several at once.
  for (const [i, key] of targets.entries()) {
    input.onProgress?.(i + 1, targets.length);
    let result: JournalSectionAiResult;
    try {
      result = await input.generate(key);
    } catch (e) {
      if (input.isFatal?.(e)) throw e;
      failed.push(key);
      continue;
    }
    if (result.source !== "ai" || !result.text.trim()) {
      failed.push(key);
      continue;
    }
    if (!stillUntouched(key)) continue;
    input.apply(key, result.text);
    summarised.push(key);
  }
  return { summarised, raw: failed.filter(stillUntouched) };
}

/** The compile button's label while the run is on. */
export function autoSummaryProgressLabel(
  current: number,
  total: number,
): string {
  return `Sammanfattar ${current} av ${total} ${total === 1 ? "rubrik" : "rubriker"}…`;
}
